/**
 * InoculationEventsList Component
 *
 * Table view of inoculation events with filtering and expandable details.
 * Part of US-P2-005: Inoculation Tracking & Production Batch Management
 *
 * Features:
 * - Filterable by strain and date range
 * - Expandable rows showing substrate allocations and spawn usage
 * - Production batch code linking
 * - Stats dashboard
 */

import { useState, useMemo } from 'react'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import SearchInput from '@/components/ui/search-input'
import {
  useInoculationEvents
} from '@/hooks/useInoculation'
import { useStrains } from '@/hooks/useLC'
import InoculationFormV2 from './InoculationFormV2'
import InoculationStatusBadge from './InoculationStatusBadge'
import {
  Plus,
  Sprout,
  AlertTriangle,
  Filter,
  ChevronDown,
  ChevronRight,
  Package,
  Beaker,
  Group
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { parseDateString } from '@/utils/dateUtils'

export default function InoculationEventsList({ user, onLogout }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [strainFilter, setStrainFilter] = useState('all')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState(new Set())

  const { events: inoculationEvents, loading, error, refetch } = useInoculationEvents({
    strainId: strainFilter !== 'all' ? strainFilter : undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined
  })
  const { strains } = useStrains()

  // Filter and search logic
  const filteredEvents = useMemo(() => {
    let result = [...inoculationEvents]

    // Search by batch code or notes
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(event =>
        event.production_batch?.batch_code?.toLowerCase().includes(search) ||
        event.notes?.toLowerCase().includes(search) ||
        event.strain?.name?.toLowerCase().includes(search)
      )
    }

    return result
  }, [inoculationEvents, searchTerm])

  const handleAddInoculation = () => {
    setIsFormOpen(true)
  }

  const handleFormSave = async () => {
    setIsFormOpen(false)
    refetch()
  }

  const toggleRowExpansion = (eventId) => {
    setExpandedRows(prev => {
      const next = new Set(prev)
      if (next.has(eventId)) {
        next.delete(eventId)
      } else {
        next.add(eventId)
      }
      return next
    })
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  if (loading) {
    return (
      <PageLayout
        title="Inoculations"
        icon={Group}
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-content-secondary">Loading...</div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout
        title="Inoculations"
        icon={Group}
        user={user}
        onLogout={onLogout}
      >
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading inoculation events: {error}
          </AlertDescription>
        </Alert>
      </PageLayout>
    )
  }

  // Header actions: search → filters → primary action
  const headerActions = (
    <>
      <SearchInput
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        placeholder="Search batches..."
        className="w-[200px]"
      />

      <Select value={strainFilter} onValueChange={setStrainFilter}>
        <SelectTrigger className="w-[160px]">
          <Filter className="h-4 w-4 mr-2" />
          <SelectValue placeholder="All Strains" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Strains</SelectItem>
          {strains.map(strain => (
            <SelectItem key={strain.id} value={strain.id}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs">{strain.code}</span>
                <span>{strain.name}</span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button onClick={handleAddInoculation}>
        <Plus className="h-4 w-4 mr-2" />
        Record Inoculation
      </Button>
    </>
  )

  return (
    <PageLayout
      title="Inoculations"
      icon={Group}
      user={user}
      onLogout={onLogout}
      headerActions={headerActions}
    >
      <div className="h-full overflow-y-auto">
      {/* Results count */}
      <div className="text-sm text-content-secondary mb-4">
        Showing {filteredEvents.length} of {inoculationEvents.length} inoculation events
      </div>

      {/* Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10"></TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Batch Code</TableHead>
              <TableHead>Strain</TableHead>
              <TableHead className="text-right">Total Blocks</TableHead>
              <TableHead className="text-right">Spawn Used (g)</TableHead>
              <TableHead>Notes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredEvents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-content-secondary">
                  {inoculationEvents.length === 0 ? (
                    <div className="flex flex-col items-center gap-2">
                      <Sprout className="h-8 w-8 text-content-tertiary" />
                      <span>No inoculation events yet</span>
                      <Button variant="outline" size="sm" onClick={handleAddInoculation}>
                        <Plus className="h-4 w-4 mr-2" />
                        Record First Inoculation
                      </Button>
                    </div>
                  ) : (
                    'No events match your filters'
                  )}
                </TableCell>
              </TableRow>
            ) : (
              filteredEvents.map(event => {
                const isExpanded = expandedRows.has(event.id)

                return (
                  <>
                    <TableRow key={event.id} className="cursor-pointer hover:bg-surface-secondary/50">
                      <TableCell onClick={() => toggleRowExpansion(event.id)}>
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-content-secondary" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-content-secondary" />
                        )}
                      </TableCell>
                      <TableCell onClick={() => toggleRowExpansion(event.id)}>
                        {formatDate(event.event_date)}
                      </TableCell>
                      <TableCell onClick={() => toggleRowExpansion(event.id)}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">
                            {event.production_batch?.batch_code || '-'}
                          </span>
                          {event.production_batch?.status && (
                            <InoculationStatusBadge status={event.production_batch.status} />
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={() => toggleRowExpansion(event.id)}>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono">
                            {event.strain?.code}
                          </Badge>
                          <span className="text-sm">{event.strain?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right" onClick={() => toggleRowExpansion(event.id)}>
                        <span className="font-medium">{event.total_blocks}</span>
                      </TableCell>
                      <TableCell className="text-right" onClick={() => toggleRowExpansion(event.id)}>
                        <span className="font-medium">
                          {event.total_spawn_grams?.toFixed(0) || '-'}
                        </span>
                      </TableCell>
                      <TableCell onClick={() => toggleRowExpansion(event.id)}>
                        <span className="text-sm text-content-secondary truncate max-w-xs block">
                          {event.notes || '-'}
                        </span>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Row Details */}
                    {isExpanded && (
                      <TableRow className="bg-surface-secondary/30">
                        <TableCell colSpan={7} className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Substrate Allocations */}
                            <div>
                              <h4 className="font-medium mb-3 flex items-center gap-2">
                                <Package className="h-4 w-4" />
                                Substrate Allocations
                              </h4>
                              <div className="space-y-2">
                                {event.allocations?.map(alloc => (
                                  <div
                                    key={alloc.id}
                                    className="flex justify-between items-center p-3 rounded-lg bg-background border"
                                  >
                                    <div>
                                      <span className="font-mono text-sm font-medium">
                                        {alloc.substrate_batch?.batch_code}
                                      </span>
                                      <p className="text-xs text-content-tertiary mt-1">
                                        {alloc.substrate_batch?.recipe?.name} •{' '}
                                        {alloc.substrate_batch?.block_format?.name}
                                      </p>
                                    </div>
                                    <Badge variant="outline">
                                      {alloc.blocks_allocated} blocks
                                    </Badge>
                                  </div>
                                )) || (
                                  <p className="text-sm text-content-tertiary">No allocations recorded</p>
                                )}
                              </div>
                            </div>

                            {/* Spawn Usage */}
                            <div>
                              <h4 className="font-medium mb-3 flex items-center gap-2">
                                <Beaker className="h-4 w-4" />
                                Spawn Usage
                              </h4>
                              <div className="space-y-2">
                                {event.spawn_usage?.map(usage => (
                                  <div
                                    key={usage.id}
                                    className="flex justify-between items-center p-3 rounded-lg bg-background border"
                                  >
                                    <div>
                                      <span className="font-mono text-sm font-medium">
                                        {usage.spawn_batch?.code}
                                      </span>
                                      <p className="text-xs text-content-tertiary mt-1">
                                        {usage.spawn_batch?.grain_type}
                                      </p>
                                    </div>
                                    <Badge variant="outline">
                                      {usage.quantity_grams?.toFixed(0)}g
                                    </Badge>
                                  </div>
                                )) || (
                                  <p className="text-sm text-content-tertiary">No spawn usage recorded</p>
                                )}
                              </div>

                              {/* Spawn Rate Info */}
                              {event.spawn_rate_percent && (
                                <div className="mt-4 p-3 rounded-lg bg-odin-blue/5 border border-odin-blue/20">
                                  <div className="flex justify-between items-center">
                                    <span className="text-sm text-content-secondary">Spawn Rate:</span>
                                    <span className="font-medium text-odin-blue">
                                      {event.spawn_rate_percent.toFixed(1)}%
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Full Notes */}
                          {event.notes && (
                            <div className="mt-4 pt-4 border-t">
                              <h4 className="font-medium mb-2">Notes</h4>
                              <p className="text-sm text-content-secondary">{event.notes}</p>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )
              })
            )}
          </TableBody>
        </Table>
      </Card>
      </div>

      {/* Form Dialog */}
      {isFormOpen && (
        <InoculationFormV2
          isOpen={isFormOpen}
          onClose={() => setIsFormOpen(false)}
          onSave={handleFormSave}
        />
      )}
    </PageLayout>
  )
}
